import { Alert, Box, Chip, Divider, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const GAME_STATUS_LABELS = {
  NOT_STARTED: 'not started',
  IN_PROGRESS: 'in progress',
  COMPLETE: 'completed',
  PLAYOFF: 'playoff',
  UNKNOWN: 'unknown'
};

const statusBreakdown = (gamesByStatus = {}) => {
  const parts = Object.entries(gamesByStatus)
    .filter(([, count]) => Number(count) > 0)
    .map(([status, count]) => `${count} ${GAME_STATUS_LABELS[status] || status.toLowerCase()}`);
  return parts.length > 0 ? ` (${parts.join(', ')})` : '';
};

const playerLabel = (player) => {
  const name = [player?.name, player?.surname].filter(Boolean).join(' ').trim();
  return name || 'Unknown player';
};

const CountRow = ({ label, value, detail = '' }) => (
  <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={2}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography component="span" variant="body2" fontWeight={600} textAlign="right">
      {value}
      {detail ? (
        <Typography component="span" variant="caption" color="text.secondary">
          {detail}
        </Typography>
      ) : null}
    </Typography>
  </Box>
);

const CountSection = ({ title, rows }) => {
  const visible = rows.filter((row) => Number(row.value) > 0 || row.always);
  if (visible.length === 0) {
    return null;
  }
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Stack gap={0.5}>
        {visible.map((row) => (
          <CountRow key={row.label} label={row.label} value={row.value} detail={row.detail} />
        ))}
      </Stack>
    </Box>
  );
};

const severityOrder = { error: 0, warning: 1, info: 2 };

const sortedWarnings = (warnings = []) =>
  [...warnings].sort(
    (left, right) => (severityOrder[left.severity] ?? 3) - (severityOrder[right.severity] ?? 3)
  );

/**
 * Read-only review of a player-replacement or venue-swap preview. Both dialogs
 * show exactly what the server reported: blocked errors, the source and
 * destination, every count, and every warning. Nothing here writes.
 *
 * With `applied`, the same layout becomes the post-apply summary: the counts
 * come from the apply response and a banner says the changes are live.
 */
const EventChangePreview = ({ result, kind, applied = false }) => {
  if (!result) {
    return null;
  }

  const counts = result.counts || {};
  const warnings = sortedWarnings(result.warnings);
  const errors = applied ? [] : result.errors || [];
  const isPlayer = kind === 'player';
  const isReserve = kind === 'reserve';

  const sectionCountSection = {
    title: 'Sections',
    rows: [
      { label: 'Sections updated', value: counts.sections },
      { label: 'Section entries updated', value: counts.sectionEntries },
      { label: 'Finalised sections skipped', value: counts.finalisedSectionsSkipped },
      { label: 'Finalised entries skipped', value: counts.finalisedEntriesSkipped }
    ]
  };

  const gamesCountSection = {
    title: 'Games and brackets',
    rows: [
      {
        label: 'Games updated',
        value: counts.games,
        detail: statusBreakdown(counts.gamesByStatus)
      },
      { label: 'Completed games skipped', value: counts.completedGamesSkipped },
      { label: 'Deleted games skipped', value: counts.deletedGamesSkipped },
      { label: 'Bracket matches updated', value: counts.bracketMatches },
      {
        label: 'Completed bracket matches skipped',
        value: counts.completedBracketMatchesSkipped
      }
    ]
  };

  const playerCountSections = [
    {
      title: 'Teams and roster',
      rows: [
        { label: 'Teams updated', value: counts.teams },
        { label: 'Roster entries updated', value: counts.roster },
        { label: 'Reserve snapshots updated', value: counts.reserves }
      ]
    },
    sectionCountSection,
    gamesCountSection
  ];

  const reserveCountSections = [
    {
      title: 'Team and reserves',
      rows: [
        { label: 'Teams updated', value: counts.teams },
        { label: 'Reserve snapshots updated', value: counts.reserves }
      ]
    },
    sectionCountSection,
    gamesCountSection
  ];

  const venueCountSections = [
    {
      title: 'Event venue',
      rows: [
        { label: 'Event venue updated', value: counts.eventVenue },
        { label: 'Locations updated', value: counts.locations },
        { label: 'Greens updated', value: counts.greens }
      ]
    },
    {
      title: 'Sections',
      rows: [
        { label: 'Rink plan entries updated', value: counts.rinkPlanEntries },
        { label: 'Sections updated', value: counts.sections }
      ]
    },
    {
      title: 'Games and brackets',
      rows: [
        {
          label: 'Games updated',
          value: counts.games,
          detail: statusBreakdown(counts.gamesByStatus)
        },
        { label: 'Green assignments changed', value: counts.greenAssignments },
        { label: 'Destination venue green updated', value: counts.venueGreenUpdates },
        { label: 'Completed games skipped', value: counts.completedGamesSkipped },
        { label: 'Deleted games skipped', value: counts.deletedGamesSkipped },
        { label: 'Section games not selected', value: counts.sectionGamesExcluded },
        { label: 'Bracket games not selected', value: counts.bracketGamesExcluded },
        { label: 'Bracket matches not selected', value: counts.bracketMatchesExcluded },
        { label: 'Completed games pinned to the old venue', value: counts.pinnedCompletedGames },
        { label: 'Games follow the event venue', value: counts.implicitGames },
        { label: 'Games left unplaced', value: counts.unplacedGames },
        { label: 'Bracket matches updated', value: counts.bracketMatches },
        {
          label: 'Completed bracket matches skipped',
          value: counts.completedBracketMatchesSkipped
        },
        { label: 'Round slot collisions', value: counts.roundSlotCollisions }
      ]
    }
  ];

  const countSections = isReserve
    ? reserveCountSections
    : isPlayer
      ? playerCountSections
      : venueCountSections;

  const promotedReserve = result.promotion?.reserve;
  const replacedMember = result.promotion?.replacedMember;
  const sourceLabel = isReserve
    ? playerLabel(promotedReserve)
    : isPlayer
      ? playerLabel(result.source)
      : result.source?.name
        ? `${result.source.name}${result.source.id ? ` (${result.source.id})` : ''}`
        : '';
  const destinationLabel = isReserve
    ? 'Active lineup'
    : isPlayer
      ? playerLabel(result.destination)
      : result.destination?.name || '';

  return (
    <Stack gap={2}>
      {applied ? (
        <Alert severity="success">
          <Typography variant="body2" fontWeight={600}>
            Changes applied
          </Typography>
          <Typography variant="caption" display="block">
            These records were written to the event. This is the summary of what changed.
          </Typography>
        </Alert>
      ) : null}

      {errors.map((error) => (
        <Alert key={`${error.code}-${error.message}`} severity="error">
          <Typography variant="body2" fontWeight={600}>
            {error.message}
          </Typography>
          {Array.isArray(error.details?.candidates) && error.details.candidates.length > 0 ? (
            <Typography variant="caption" display="block">
              Candidates:{' '}
              {error.details.candidates
                .map((candidate) =>
                  isPlayer ? playerLabel(candidate) : candidate.name || candidate.id
                )
                .join(', ')}
            </Typography>
          ) : null}
        </Alert>
      ))}

      {!applied && result.alreadyApplied ? (
        <Alert severity="info">
          This change has already been applied to the event. Nothing will be written.
        </Alert>
      ) : null}

      <Box
        display="flex"
        alignItems="center"
        gap={1}
        flexWrap="wrap"
        sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="body2" fontWeight={600}>
          {sourceLabel}
        </Typography>
        <ArrowForwardIcon fontSize="small" color="action" />
        <Typography variant="body2" fontWeight={600}>
          {destinationLabel}
        </Typography>
        {!isPlayer &&
        !isReserve &&
        result.green &&
        (Number(counts.greenAssignments) > 0 || Number(counts.venueGreenUpdates) > 0) ? (
          <Chip size="small" label={`Green ${result.green}`} variant="outlined" />
        ) : null}
        {isPlayer && result.destination?.created ? (
          <Chip size="small" label="New directory player" color="info" />
        ) : null}
        {isPlayer && result.destination?.source === 'directory' ? (
          <Chip size="small" label="From directory" variant="outlined" />
        ) : null}
        {isPlayer && result.destination?.status ? (
          <Chip size="small" label={result.destination.status} variant="outlined" />
        ) : null}
        {isReserve && result.promotion?.addedToLineup ? (
          <Chip size="small" label="Free lineup slot" color="info" />
        ) : null}
      </Box>

      {isReserve ? (
        <Typography variant="caption" color="text.secondary">
          {replacedMember
            ? `${playerLabel(replacedMember)} moves to the reserves list.`
            : 'The lineup had a free slot, so no player moves to the reserves list.'}
        </Typography>
      ) : null}

      {isPlayer && Array.isArray(result.source?.teams) && result.source.teams.length > 0 ? (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Teams:{' '}
            {result.source.teams
              .map((team) =>
                team.role === 'reserve' ? `${team.name || team.id} (reserve)` : team.name || team.id
              )
              .join(', ')}
          </Typography>
        </Box>
      ) : null}

      {isReserve && result.team ? (
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Team name
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            justifyContent="space-between">
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {result.team.oldName || '(no name)'} → {result.team.newName || '(no name)'}
            </Typography>
            {result.team.customNamePreserved ? (
              <Chip size="small" label="Custom name kept" variant="outlined" />
            ) : null}
          </Box>
        </Box>
      ) : null}

      {isPlayer && Array.isArray(result.teams) && result.teams.length > 0 ? (
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Team names
          </Typography>
          <Stack gap={0.75}>
            {result.teams.map((team) => (
              <Box
                key={team.id}
                display="flex"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
                justifyContent="space-between">
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {team.oldName || '(no name)'} → {team.newName || '(no name)'}
                </Typography>
                {team.role === 'reserve' ? (
                  <Chip size="small" label="Reserve list only" variant="outlined" />
                ) : null}
                {team.customNamePreserved ? (
                  <Chip size="small" label="Custom name kept" variant="outlined" />
                ) : null}
              </Box>
            ))}
          </Stack>
        </Box>
      ) : null}

      <Divider />

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }} gap={2}>
        {countSections.map((section) => (
          <CountSection key={section.title} title={section.title} rows={section.rows} />
        ))}
      </Box>

      {warnings.length > 0 ? (
        <>
          <Divider />
          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Warnings
            </Typography>
            <Stack gap={1}>
              {warnings.map((item) => (
                <Alert
                  key={`${item.code}-${item.message}`}
                  severity={item.severity === 'error' ? 'error' : item.severity}>
                  {item.message}
                </Alert>
              ))}
            </Stack>
          </Box>
        </>
      ) : null}
    </Stack>
  );
};

export default EventChangePreview;
